import { Link } from "react-router-dom";
import { Reveal, LineReveal, ParallaxImage } from "../components/motion";
import CTABand from "../components/CTABand";
import { useSEO, usePageView } from "../lib/seo";

export default function About() {
  useSEO({
    title: "About Tamika — Certified Infant & Child Sleep Consultant",
    description: "Meet Tamika: certified infant and child sleep consultant and professional nanny of 6+ years. Gentle, practical, tailored sleep support for your family.",
    schema: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Tamika",
      jobTitle: "Certified infant & child sleep consultant",
      worksFor: { "@type": "Organization", name: "The Nanny Sleep Co." },
    },
  });
  usePageView("/about");

  return (
    <div data-testid="about-page">
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="overline-label mb-5">About</p>
            <LineReveal
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink"
              lines={["Hi, I'm Tamika."]}
            />
            <Reveal delay={0.4}>
              <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.05rem]">
                <p>
                  I'm the founder of The Nanny Sleep Co. — a certified infant and toddler sleep consultant, and before that, a professional nanny for over six years.
                </p>
                <p>
                  In those years I cared for children of all ages and stood beside countless families through their biggest milestones — and their hardest nights. I know how overwhelming it feels when your little one isn't sleeping, and how lonely 3am can be.
                </p>
                <p>
                  So I built the support I wished those families had: gentle, nurturing, and focused on routines that support your baby's needs <em>and</em> your wellbeing. No harsh methods, no one-size-fits-all plans, no judgement about how you've survived until now.
                </p>
                <p>
                  Because when your little one sleeps well, your whole family thrives.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/sleep-assessment" data-testid="about-assessment-cta" className="btn-primary">Start with the assessment</Link>
                <Link to="/how-i-work" data-testid="about-method-cta" className="btn-secondary">How I work</Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-4 bg-sage-light rounded-[2.5rem] -rotate-2" aria-hidden="true" />
              <ParallaxImage
                src="https://images.squarespace-cdn.com/content/v1/68b7e04ed5afe32710b42d83/8cef08b2-5d57-44a4-bb44-244be6da555c/IMG_5703.jpg"
                alt="Tamika, founder of The Nanny Sleep Co."
                className="relative rounded-[2rem] aspect-[4/5] shadow-[0_24px_60px_rgba(44,40,37,0.14)]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-warm/70 border-y border-ink/[0.05]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Reveal>
              <div className="card-soft p-8 h-full">
                <h2 className="font-serif text-2xl font-medium text-ink mb-4">My approach</h2>
                <p className="text-ink-soft leading-relaxed text-sm sm:text-base">
                  Gentle, responsive and tailored. I take time to understand your baby's unique temperament, your family's routines and your parenting style — then together we create a sleep plan that feels comfortable, sustainable and realistic for your life.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card-soft p-8 h-full">
                <h2 className="font-serif text-2xl font-medium text-ink mb-4">My promise</h2>
                <p className="text-ink-soft leading-relaxed text-sm sm:text-base">
                  To empower you to feel secure, supported and less overwhelmed — with clear guidance, honest expectations, and ongoing support while you implement. You'll never be left guessing, and you'll never be pressured.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand title="Let's find your family's way back to sleep" />
    </div>
  );
}
